/**
 * TA Cockpit → Tracking → Hiring. The trimester's roles, and what became of them.
 *
 * WHAT THIS IS NOT: the demand board. Collection moved out of the cockpit
 * (Theresa, Sep 2026), so there is nothing here that adds a role, submits a
 * unit, drags a seat into a team or moves a start date — and no current team
 * members either. What survives is the half that answers "did we deliver": the
 * unit and team tree with its running deltas, and the timeline.
 *
 * THE TREE AND THE DELTAS ARE THE DEMAND BOARD'S, reused rather than rebuilt.
 * `buildTree` groups the roles, `tally` computes the per-month roll-up and the
 * carried shortfall, and `GroupRow` draws it — so the two tabs cannot come to
 * disagree about what a unit's plan says while both are alive.
 *
 * THE ROSTER IS DROPPED AT THE DOOR. `buildTree` also places people, and the
 * cleanest way to have none is to hand it a board with none rather than to skip
 * them at render time — a member that reaches a node is a member some later
 * change will start drawing again.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { fetchDemandBoard } from '../../../../services/taDemandApi';
import type { BoardRole, DemandBoard } from '../../../../types/taDemand';
import { buildTracks, heads, rolesOfDept, rolesOfUnit } from '../../demand/demandModel';
import { buildTree } from '../../demand/demandTreeModel';
import { GroupRow, HeaderRow, MONTH_W, GROUP_W } from '../../demand/DemandChrome';
import { ScrollSync } from '../../demand/ScrollSync';
import { HiringRow } from './HiringRow';
import { FoldedRoles } from './FoldedRoles';
import { SCOPES, scopeWindow, splitRoles, targetWindow, type ViewScope } from './hiringScope';
import { Button } from '@finn-auto/design-system/atoms/button';
import { cn } from '../../../../../../shared';

/**
 * Narrower than the demand board's 740. That column carried level, FTE, reason,
 * status and a row of controls; here it carries a title, a badge and a glyph,
 * and every pixel it gives back is a month the reader can see.
 */
const NAME_W = 430;

export function HiringTable({ period }: { period: string }) {
  const [board, setBoard] = useState<DemandBoard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showPast, setShowPast] = useState(false);
  const [scope, setScope] = useState<ViewScope>('year');
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const isOpen = (key: string, dflt = false) => open[key] ?? dflt;
  const toggle = (key: string, dflt = false) =>
    setOpen((o) => ({ ...o, [key]: !(o[key] ?? dflt) }));

  const load = useCallback(() => {
    fetchDemandBoard(period)
      .then((b) => {
        setBoard(b);
        setError(null);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [period]);
  useEffect(() => load(), [load]);

  const hiringOnly = useMemo(() => (board ? { ...board, roster: [] } : null), [board]);
  const tree = useMemo(() => (hiringOnly ? buildTree(hiringOnly) : []), [hiringOnly]);
  /**
   * ⚠️ ONLY THE COLUMNS ARE FILTERED — never the `months` every span is
   * computed against.
   *
   * `roleSpan` reads `months` to decide whether a bar's end is on the board and
   * what the last column is, so handing it a narrowed list would re-anchor
   * every bar to the visible edge and change where roles appear to sit. The
   * scope decides what is DRAWN; a bar reaching past it is simply clipped,
   * which is what a narrowed view should do.
   */
  const visibleMonths = useMemo(() => {
    if (!board) return [];
    const w = scopeWindow(scope, period);
    return w ? board.months.filter((m) => m.key >= w.from && m.key <= w.to) : board.months;
  }, [board, scope, period]);

  const tracks = useMemo(
    /*
      A chosen trimester is an explicit range, so nothing inside it is collapsed
      as "past" — the toggle would otherwise hide the first months of exactly
      the round somebody asked to look at.
    */
    () => buildTracks(visibleMonths, scope === 'year' ? showPast : true),
    [visibleMonths, showPast, scope],
  );
  const window = useMemo(() => targetWindow(period), [period]);
  const template = useMemo(
    () => `${NAME_W}px ${tracks.map((t) => (t.kind === 'group' ? `${GROUP_W}px` : `${MONTH_W}px`)).join(' ')}`,
    [tracks],
  );
  const minWidth = useMemo(
    () => NAME_W + tracks.reduce((s, t) => s + (t.kind === 'group' ? GROUP_W : MONTH_W), 0),
    [tracks],
  );
  /*
    What the collapsed past-months group says it is hiding. Counted the same way
    the demand board counts it, off the same helpers — a second definition would
    let the two tabs report different numbers for the same months.
  */
  const pastHires = useMemo(() => {
    if (!board) return 0;
    const past = new Set(board.months.filter((m) => m.past).map((m) => m.key));
    return heads(tree.flatMap(rolesOfDept).filter((r) => r.month !== null && past.has(r.month)));
  }, [board, tree]);

  if (error) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-900">
        <AlertCircle className="h-4 w-4 shrink-0" />
        The hiring plan could not be loaded. {error}
      </div>
    );
  }
  if (!board) {
    return (
      <div className="flex h-40 items-center justify-center gap-2 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading the plan…
      </div>
    );
  }

  const months = board.months;
  const currentMonth = board.currentMonth;
  const row = (r: BoardRole, depth: number) => (
    <HiringRow
      key={r.id}
      role={r}
      tracks={tracks}
      months={months}
      currentMonth={currentMonth}
      depth={depth}
    />
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="mr-1 text-xs text-gray-500">Show</span>
        {SCOPES.map((s) => (
          <Button
            key={s.value}
            variant={scope === s.value ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setScope(s.value)}
            aria-pressed={scope === s.value}
            className={cn('!rounded-full', scope === s.value && 'font-semibold')}
          >
            {s.label}
          </Button>
        ))}
        {scope !== 'year' && (
          <span className="ml-1 text-xs text-gray-400">
            {scope.toUpperCase()} and the three months it hires for
          </span>
        )}
      </div>

      <ScrollSync className="rounded-xl border border-gray-200 bg-white" width={minWidth}>
      <div style={{ minWidth, ['--demand-tmpl' as string]: template }}>
        <HeaderRow tracks={tracks} showPast={showPast} onTogglePast={() => setShowPast((v) => !v)} pastHires={pastHires} />
        {tree.map((dept) => {
          const dOpen = isOpen(dept.key);
          return (
            <div key={dept.key}>
              <GroupRow
                tracks={tracks}
                months={months}
                currentMonth={currentMonth}
                depth={0}
                open={dOpen}
                onClick={() => toggle(dept.key)}
                tone="dept"
                roles={dept.units.flatMap(rolesOfUnit)}
                label={<span className="text-[13px] font-semibold text-gray-900">{dept.name}</span>}
              />
              {dOpen &&
                dept.units.map((unit) => {
                  const uOpen = isOpen(unit.key);
                  const loose = splitRoles(unit.looseRoles, window);
                  return (
                    <div key={unit.key}>
                      <GroupRow
                        tracks={tracks}
                        months={months}
                        currentMonth={currentMonth}
                        depth={1}
                        open={uOpen}
                        onClick={() => toggle(unit.key)}
                        tone="unit"
                        roles={rolesOfUnit(unit)}
                        label={<span className="text-[13px] font-medium text-gray-800">{unit.name}</span>}
                      />
                      {uOpen && (
                        <>
                          {unit.teams.map((team) => {
                            const tOpen = isOpen(team.key, true);
                            const split = splitRoles(team.roles, window);
                            if (!team.roles.length) return null;
                            return (
                              <div key={team.key}>
                                <GroupRow
                                  tracks={tracks}
                                  months={months}
                                  currentMonth={currentMonth}
                                  depth={2}
                                  open={tOpen}
                                  onClick={() => toggle(team.key, true)}
                                  tone="team"
                                  roles={team.roles}
                                  label={<span className="text-[13px] text-gray-700">{team.name}</span>}
                                />
                                {tOpen && split.live.map((r) => row(r, 3))}
                                {tOpen && (
                                  <FoldedRoles
                                    roles={split.later}
                                    tracks={tracks}
                                    months={months}
                                    currentMonth={currentMonth}
                                    depth={3}
                                    open={isOpen(`${team.key}|later`)}
                                    onToggle={() => toggle(`${team.key}|later`)}
                                    label={(n) => `${n} starting after this round`}
                                  />
                                )}
                              </div>
                            );
                          })}
                          {loose.live.map((r) => row(r, 2))}
                          <FoldedRoles
                            roles={loose.later}
                            tracks={tracks}
                            months={months}
                            currentMonth={currentMonth}
                            depth={2}
                            open={isOpen(`${unit.key}|later`)}
                            onToggle={() => toggle(`${unit.key}|later`)}
                            label={(n) => `${n} starting after this round`}
                          />
                          <FoldedRoles
                            roles={unit.cancelled ?? []}
                            tracks={tracks}
                            months={months}
                            currentMonth={currentMonth}
                            depth={2}
                            open={isOpen(`${unit.key}|cancelled`)}
                            onToggle={() => toggle(`${unit.key}|cancelled`)}
                            label={(n) => `${n} cancelled`}
                            cancelled
                          />
                        </>
                      )}
                    </div>
                  );
                })}
            </div>
          );
        })}
      </div>
      </ScrollSync>
    </div>
  );
}
